// Calendar days for the habit rules. A day is a plain "YYYY-MM-DD" string in the person's own time zone,
// taken once from a timestamp; all day arithmetic then runs on the string through UTC dates, so summer
// time changes never shift a day and the result is the same on every machine.

/** A calendar day, "YYYY-MM-DD". */
export type Day = string;

/** ISO weekday: 1 Monday to 7 Sunday. */
export type IsoWeekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

const DAY_MS = 86_400_000;
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const formatters = new Map<string, Intl.DateTimeFormat>();
function formatter(timeZone: string | undefined): Intl.DateTimeFormat {
  const key = timeZone ?? '';
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat('en-CA', {
      timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    });
    formatters.set(key, f);
  }
  return f;
}

const cache = new Map<string, { day: Day; minute: number }>();
const CACHE_SIZE = 5000;

/** The day and the minute of the day (0 to 1439) of a moment, in a time zone (the device's when left out). */
export function localDayTime(ms: number, timeZone?: string): { day: Day; minute: number } {
  // Every rule asks for the day of every session, often; formatting dates is slow, so recent answers are kept.
  const key = `${timeZone ?? ''}|${ms}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const parts: Record<string, string> = {};
  for (const p of formatter(timeZone).formatToParts(new Date(ms))) parts[p.type] = p.value;
  const out = { day: `${parts.year}-${parts.month}-${parts.day}`, minute: Number(parts.hour) * 60 + Number(parts.minute) };
  if (cache.size >= CACHE_SIZE) cache.clear();
  cache.set(key, out);
  return out;
}

/** The calendar day of a moment in a time zone (the device's when left out). */
export function localDay(ms: number, timeZone?: string): Day {
  return localDayTime(ms, timeZone).day;
}

export function isDay(value: unknown): value is Day {
  if (typeof value !== 'string') return false;
  const m = DAY_RE.exec(value);
  if (!m) return false;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3]);
}

/** Days since 1970-01-01. */
export function dayNumber(day: Day): number {
  const m = DAY_RE.exec(day);
  if (!m) throw new Error(`Not a day: ${day}`);
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / DAY_MS;
}

export function fromDayNumber(n: number): Day {
  return new Date(n * DAY_MS).toISOString().slice(0, 10);
}

export function addDays(day: Day, n: number): Day {
  return fromDayNumber(dayNumber(day) + n);
}

/** b minus a, in days. */
export function daysBetween(a: Day, b: Day): number {
  return dayNumber(b) - dayNumber(a);
}

export function isoWeekday(day: Day): IsoWeekday {
  return ((((dayNumber(day) + 3) % 7) + 7) % 7 + 1) as IsoWeekday; // 1970-01-01 was a Thursday
}

/** The Monday of the day's week (weeks run Monday to Sunday). */
export function weekStart(day: Day): Day {
  return addDays(day, 1 - isoWeekday(day));
}

/** "YYYY-MM". */
export function monthOf(day: Day): string {
  return day.slice(0, 7);
}

export function monthStart(day: Day): Day {
  return `${monthOf(day)}-01`;
}

export function monthEnd(day: Day): Day {
  const [y, m] = day.split('-').map(Number) as [number, number];
  return fromDayNumber(Date.UTC(y, m, 1) / DAY_MS - 1);
}

/** The days from a to b, both included (empty when b is before a). */
export function dayRange(a: Day, b: Day): Day[] {
  const out: Day[] = [];
  for (let n = dayNumber(a), end = dayNumber(b); n <= end; n++) out.push(fromDayNumber(n));
  return out;
}

/** "21:00" to minutes of the day; null when it is not a valid time. */
export function parseTime(time: string): number | null {
  const m = /^(\d{2}):(\d{2})$/.exec(time);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  return h < 24 && min < 60 ? h * 60 + min : null;
}

export const WEEKDAY_NAMES: Record<IsoWeekday, string> = {
  1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday', 7: 'Sunday',
};
